import { apiGet, apiPatch, apiPost, apiDelete, apiUpload, getAuthCredentials, getBaseUrl } from './api';
import type {
  DateObiectivInvestitie,
  FisierListaInvestitii,
  HalCollection,
  ObiectivInvestitie,
  Project,
  RaspunsFisiereListaInvestitii,
  RaspunsListaInvestitii,
  SursaFinantare,
} from '@/types';

// ============================================================================
// Lista de investiții — anexa la buget, adusă din `dossier.investment_*`.
//
// Un singur apel, către un singur endpoint. Drepturile NU se verifică aici:
// backendul trimite lista întreagă oricui e autentificat (e anexa publică a unei
// hotărâri de consiliu), dar taie el legătura spre proiect pentru obiectivele din
// afara portofoliului utilizatorului — `legatura: 'ascuns'` sosește deja fără
// `proiectId` și fără `proiectNume`. Nu ascunde nimic interfața.
//
// Sumele sosesc în LEI. Documentul oficial e în mii lei, deci tot ce se afișează
// trece prin `miiLei`. Backendul le castează `::float8`, altfel node-postgres ar
// trimite `numeric` ca șir și adunarea în JS ar concatena.
// ============================================================================

export async function getListaInvestitii(semnal?: AbortSignal): Promise<RaspunsListaInvestitii> {
  const raspuns = await apiGet<RaspunsListaInvestitii>('/auth/data/lista-investitii', {
    signal: semnal,
  });
  return normalizeazaRaspuns(raspuns);
}

// Plasă de siguranță: un răspuns fără `obiective` (proxy, eroare intermediară) nu
// trebuie să pice pe `.map`. Câmpurile adăugate odată cu Administrarea primesc
// valori neutre dacă lipsesc: un tab deschis pe bundle-ul nou poate întâlni, cât
// durează o repornire, backendul vechi — și atunci `undefined` ar trece drept dată.
function normalizeazaRaspuns(r: RaspunsListaInvestitii): RaspunsListaInvestitii {
  const v = r?.versiune ?? null;
  return {
    versiune: v
      ? {
          ...v,
          modificataLa: v.modificataLa ?? null,
          modificataDe: v.modificataDe ?? null,
          totalCurent: v.totalCurent ?? v.totalAn,
          nrObiectiveCurent: v.nrObiectiveCurent ?? v.nrObiective,
        }
      : null,
    obiective: Array.isArray(r?.obiective) ? r.obiective.map(normalizeazaObiectiv) : [],
    fisier: r?.fisier ? normalizeazaFisier(r.fisier) : null,
    capitole: r?.capitole ?? {},
    sectiuni: r?.sectiuni ?? {},
    surse: r?.surse ?? {},
  };
}

function normalizeazaObiectiv(o: ObiectivInvestitie): ObiectivInvestitie {
  return {
    ...o,
    surse: o.surse ?? {},
    adaugatManual: o.adaugatManual === true,
    modificatLa: o.modificatLa ?? null,
    modificatDe: o.modificatDe ?? null,
  };
}

function normalizeazaFisier(f: FisierListaInvestitii): FisierListaInvestitii {
  return {
    ...f,
    sursa: f.sursa === 'incarcat' ? 'incarcat' : 'initial',
    numeOriginal: f.numeOriginal ?? null,
    incarcatDe: f.incarcatDe ?? null,
    incarcatLa: f.incarcatLa ?? null,
    verificatPentruRevizia: f.verificatPentruRevizia ?? null,
    verificatLa: f.verificatLa ?? null,
  };
}

// Numele sub care se salvează descărcarea: cel original al unei încărcări, altfel
// numele registrului inițial.
export function numeDescarcare(f: FisierListaInvestitii): string {
  return f.sursa === 'incarcat' && f.numeOriginal ? f.numeOriginal : f.nume;
}

// Descărcarea registrului oficial (.xlsx). Merge prin fetch cu antetul de
// autorizare, nu prin `window.open`: endpointul e sub /auth/data, iar o fereastră
// nouă n-ar duce cu ea credențialele — ar primi 401 și utilizatorul ar vedea o
// pagină albă cu JSON, nu un fișier.
export async function descarcaFisierListaInvestitii(numeFisier: string): Promise<void> {
  const raspuns = await fetch(`${getBaseUrl()}/auth/data/lista-investitii/fisier`, {
    method: 'GET',
    headers: { Authorization: `Basic ${getAuthCredentials()}` },
  });

  if (!raspuns.ok) {
    if (raspuns.status === 404) {
      throw new Error('Fișierul nu este disponibil pe server.');
    }
    if (raspuns.status === 401 || raspuns.status === 403) {
      throw new Error('Sesiunea nu mai este validă. Reautentificați-vă și încercați din nou.');
    }
    throw new Error('Descărcarea nu a reușit. Încercați din nou.');
  }

  const blob = await raspuns.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Numele din antet are întâietate: între încărcarea paginii și clic, un admin
  // poate să fi activat alt fișier — iar serverul știe ce trimite, pagina nu.
  a.download = numeDinContentDisposition(raspuns.headers.get('Content-Disposition')) ?? numeFisier;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// `filename*=UTF-8''…` (RFC 5987) — singura formă care păstrează diacriticele.
// `filename="…"` simplu e ignorat: backendul pune acolo un nume generic ASCII.
function numeDinContentDisposition(antet: string | null): string | null {
  const m = antet?.match(/filename\*=UTF-8''([^;]+)/i);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1].trim()) || null;
  } catch {
    return null;
  }
}

// ============== ADMINISTRARE (doar administratori) ==============
// Toate scrierile trec prin `/auth/...`, deci prin ramura de axios „gol" din
// api.ts: erorile sosesc ca AxiosError brut, cu `response.status` și
// `response.data.message` — de citit cu `mesajEroareScriere`, nu ca ApiError.

export async function actualizeazaObiectiv(
  id: number,
  date: DateObiectivInvestitie
): Promise<ObiectivInvestitie> {
  const r = await apiPatch<{ obiectiv: ObiectivInvestitie }>(
    `/auth/data/lista-investitii/obiective/${id}`,
    date
  );
  return normalizeazaObiectiv(r.obiectiv);
}

export async function adaugaObiectiv(date: DateObiectivInvestitie): Promise<ObiectivInvestitie> {
  const r = await apiPost<{ obiectiv: ObiectivInvestitie }>('/auth/data/lista-investitii/obiective', date);
  return normalizeazaObiectiv(r.obiectiv);
}

export async function stergeObiectiv(id: number): Promise<void> {
  await apiDelete<{ ok: boolean }>(`/auth/data/lista-investitii/obiective/${id}`);
}

// Înlocuiește DOAR fișierul servit la „Descarcă Excel". Cifrele din tabel nu se
// reimportă din el — se schimbă numai prin editare.
export async function incarcaFisierLista(file: File): Promise<FisierListaInvestitii> {
  const r = await apiUpload<{ fisier: FisierListaInvestitii }>('/auth/data/lista-investitii/fisier', file);
  return normalizeazaFisier(r.fisier);
}

export async function getFisiereLista(): Promise<RaspunsFisiereListaInvestitii> {
  const r = await apiGet<RaspunsFisiereListaInvestitii>('/auth/data/lista-investitii/fisiere');
  return {
    fisiere: Array.isArray(r?.fisiere) ? r.fisiere : [],
    initial: r?.initial ?? { nume: '', marime: null, disponibil: false, activ: true },
  };
}

// `'initial'` = revino la registrul inițial (dezactivează toate încărcările).
export async function activeazaFisierLista(id: number | 'initial'): Promise<FisierListaInvestitii> {
  const r = await apiPost<{ fisier: FisierListaInvestitii }>(
    `/auth/data/lista-investitii/fisiere/${id}/activeaza`
  );
  return normalizeazaFisier(r.fisier);
}

// Proiectele de care se poate lega un obiectiv. Pentru admin, endpointul întoarce
// toate proiectele active — exact mulțimea pe care o acceptă și PATCH-ul.
export async function getProiecteLegabile(): Promise<{ id: number; nume: string }[]> {
  const r = await apiGet<HalCollection<Project>>('/auth/data/projects');
  return (r?._embedded?.elements ?? []).map((p) => ({ id: p.id, nume: p.name }));
}

// 409 la PATCH: altcineva a salvat obiectivul între timp. Întoarce rândul proaspăt
// din răspuns, ca formularul să-l poată reîncărca.
export function conflictObiectiv(
  eroare: unknown
): { mesaj: string; obiectiv: ObiectivInvestitie | null } | null {
  const e = eroare as {
    response?: { status?: number; data?: { message?: unknown; obiectiv?: ObiectivInvestitie } };
  } | null;
  if (e?.response?.status !== 409) return null;
  const d = e.response.data;
  return {
    mesaj: typeof d?.message === 'string' && d.message.trim()
      ? d.message
      : 'Obiectivul a fost modificat între timp de altcineva.',
    obiectiv: d?.obiectiv ? normalizeazaObiectiv(d.obiectiv) : null,
  };
}

export function mesajEroareScriere(eroare: unknown, implicit: string): string {
  const e = eroare as { response?: { status?: number; data?: { message?: unknown } } } | null;
  const mesajServer = e?.response?.data?.message;
  if (typeof mesajServer === 'string' && mesajServer.trim()) return mesajServer;
  if (e?.response?.status === 401) {
    return 'Sesiunea nu mai este validă. Reautentificați-vă și încercați din nou.';
  }
  if (e?.response?.status === 403) return 'Doar administratorii pot modifica lista de investiții.';
  if (e?.response?.status === 413) return 'Fișierul este prea mare pentru server.';
  return implicit;
}

// ============== SUME ÎN FORMULAR (mii lei, format românesc) ==============

// Valoarea în lei → text editabil în mii lei. Fără separator de mii când nu are
// zecimale ar ieși „1.234", pe care `parseazaMiiLei` îl refuză ca ambiguu; de
// aceea mereu cu virgulă și cel puțin două zecimale. Până la 5 zecimale, ca
// orice sumă în lei cu bani să se întoarcă neschimbată.
export function miiLeiEditabil(lei: number | null | undefined): string {
  if (lei === null || lei === undefined) return '';
  return (lei / 1000).toLocaleString('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });
}

// Text în mii lei → lei (2 zecimale). Regula, una singură și deterministă:
//   - și „." și „," → „." e separator de mii, „," e zecimal   (1.234,56);
//     „." doar înaintea virgulei și doar pe grupe de câte 3 cifre
//   - doar „,"      → zecimal                                  (1234,56)
//   - doar „."      → zecimal                                  (1234.56)
//     EXCEPȚIE: un singur „." urmat de exact 3 cifre („1.234") e refuzat ca
//     ambiguu — la bani, a ghici între 1,234 și 1234 mii lei e mai rău decât a
//     întreba.
//   - mai multe „." fără „," → separatoare de mii             (1.234.567);
//     tot pe grupe de câte 3 cifre, altfel refuzat
// Rotunjirea se face în LEI, nu în mii: 1234.56 * 1000 dă 1234560.0000000002.
export function parseazaMiiLei(
  text: string
): { lei: number | null; eroare?: undefined } | { lei?: undefined; eroare: string } {
  const t = text.replace(/[\s\u00a0']/g, '');
  if (t === '') return { lei: null };
  if (!/^-?[\d.,]+$/.test(t)) return { eroare: 'Doar cifre, cu „," sau „." pentru zecimale.' };

  const areVirgula = t.includes(',');
  const puncte = (t.match(/\./g) ?? []).length;
  let normal: string;
  // Punctele de mii se șterg doar dacă grupează corect câte 3 cifre; altfel
  // „1,234.56" (format englezesc lipit) sau „1.23.4" ar trece tăcut ca altă sumă.
  const grupareMii = /^-?\d{1,3}(\.\d{3})+$/;
  const eroareGrupare = { eroare: `„${t}": „." se folosește doar ca separator de mii (1.234.567,89).` };
  if (areVirgula) {
    if ((t.match(/,/g) ?? []).length > 1) return { eroare: 'Prea multe virgule.' };
    const [intreg, zecimale] = t.split(',');
    if (zecimale.includes('.') || (intreg.includes('.') && !grupareMii.test(intreg))) {
      return eroareGrupare;
    }
    normal = `${intreg.replace(/\./g, '')}.${zecimale}`;
  } else if (puncte > 1) {
    if (!grupareMii.test(t)) return eroareGrupare;
    normal = t.replace(/\./g, '');
  } else if (puncte === 1 && /^-?\d{1,3}\.\d{3}$/.test(t)) {
    return { eroare: `„${t}" e ambiguu: scrieți ${t.replace('.', '')} sau ${t.replace('.', ',')}.` };
  } else {
    normal = t;
  }

  const miiLeiValoare = Number(normal);
  if (!Number.isFinite(miiLeiValoare) || normal === '-' || normal === '.') {
    return { eroare: 'Număr invalid.' };
  }
  const lei = Math.round(miiLeiValoare * 1000 * 100) / 100;
  if (Math.abs(lei) > 1e13) return { eroare: 'Suma este prea mare.' };
  return { lei: Object.is(lei, -0) ? 0 : lei };
}

// ============== AJUTOARE DE AFIȘARE ==============

// Data lungă („16 septembrie 2026") sau null. Singura cale prin care paginile
// listei formatează date: `new Date(null)` nu aruncă, dă 1 ianuarie 1970 — și ar
// apărea tăcut „verificat la 1970". Cine primește null nu afișează nimic.
export function dataLunga(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Idem, cu ora — pentru „modificat la … de …" și istoricul încărcărilor.
export function dataOra(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function marimeFisier(octeti: number | null | undefined): string {
  if (octeti === null || octeti === undefined) return '—';
  if (octeti < 1024 * 1024) return `${Math.max(1, Math.round(octeti / 1024))} KB`;
  return `${(octeti / 1024 / 1024).toLocaleString('ro-RO', { maximumFractionDigits: 1 })} MB`;
}

// ============== AJUTOARE DE AFIȘARE (sume) ==============

// Mii lei, cu două zecimale — unitatea documentului oficial. Intrarea e în lei.
export function miiLei(lei: number | null | undefined): string {
  if (lei === null || lei === undefined) return '—';
  return (lei / 1000).toLocaleString('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Forma scurtă, pentru capul de pagină: „369,56 mil. lei".
export function leiScurt(lei: number): string {
  const mil = lei / 1_000_000;
  if (Math.abs(mil) >= 1) {
    return `${mil.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mil. lei`;
  }
  return `${(lei / 1000).toLocaleString('ro-RO', { maximumFractionDigits: 0 })} mii lei`;
}

export function sumaSurselor(o: ObiectivInvestitie): number {
  return Object.values(o.surse).reduce((t, s) => t + (s ?? 0), 0);
}

// Excelul însuși poate avea surse care nu însumează totalul (r7a, rândul 58: 190
// trecut și la Buget județ, și la Credite externe). Se ARATĂ, nu se corectează —
// pagina redă anexa, nu o rescrie. Pragul de 0,5 lei ține de virgula mobilă.
export function surseleNuSeInchid(o: ObiectivInvestitie): boolean {
  const keys = Object.keys(o.surse) as SursaFinantare[];
  if (keys.length === 0) return false;
  return Math.abs(sumaSurselor(o) - o.alocatAn) > 0.5;
}

export function esteCheltuialaVerde(o: ObiectivInvestitie): boolean {
  return (o.eticheta ?? '').toLowerCase().includes('verde');
}

// Textul de căutare al unui rând, calculat o singură dată la încărcare: filtrarea
// rulează la fiecare tastă peste 152 de rânduri, nu are rost să recompună șirul.
export function textCautare(o: ObiectivInvestitie): string {
  return [o.nrFisa, o.denumire, o.beneficiar ?? '', o.subgrup ?? '', o.proiectNume ?? '']
    .join(' ')
    .toLowerCase();
}

// `apiGet` folosește axios „gol" pentru prefixele /auth/, deci interceptorul care
// traduce erorile nu rulează: aici sosește un AxiosError brut.
export function mesajEroareLista(eroare: unknown): string {
  const e = eroare as { response?: { status?: number; data?: { message?: unknown } } } | null;
  const mesajServer = e?.response?.data?.message;
  if (typeof mesajServer === 'string' && mesajServer.trim()) return mesajServer;
  if (e?.response?.status === 401 || e?.response?.status === 403) {
    return 'Sesiunea nu mai este validă. Reautentificați-vă și încercați din nou.';
  }
  return 'Lista de investiții nu a putut fi încărcată. Încercați din nou.';
}
